import { useCompetitionExportRegistrationsQuery } from '../export-registrations/queries';
import { ConfirmDialog } from '~/components/ConfirmDialog/ConfirmDialog';
import { ContextMenu } from '~/components/ContextMenu/ContextMenu';
import { usePowerliftingLive } from '~/hooks/usePowerliftingLive';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useDeleteCompetitionMutation } from '../mutations';
import { insertOnCondition } from '~/utils/array';
import { useCompetitionQuery } from '../queries';
import { useDialog } from '~/hooks/useDialog';
import { useParams } from '~/hooks/useParams';
import { Fragment } from 'react/jsx-runtime';
import { useNavigate } from 'react-router';
import { idParamSchema } from '~/schemas';
import { toast } from 'react-toastify';

function downloadCsv(data: string, filename: string) {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

interface Props {
  isResultsPage?: boolean;
}

export function Menu({ isResultsPage }: Props) {
  const { id } = useParams(idParamSchema.parse);
  const { data: competition } = useCompetitionQuery(id);
  const { can } = useAuthentication();

  const { refetch } = useCompetitionExportRegistrationsQuery(id);

  async function handleExport() {
    const { data } = await refetch();
    if (data) {
      downloadCsv(data, `${competition?.name.toLowerCase()}-anmälda-lyftare.csv`);
    }
  }

  const deleteMutation = useDeleteCompetitionMutation();
  const powerlifting = usePowerliftingLive();
  const navigate = useNavigate();
  const dialog = useDialog();

  async function handleDelete() {
    await toast.promise(deleteMutation.mutateAsync(id), {
      success: 'Tävlingen har tagits bort',
      pending: 'Tar bort tävling...',
    });

    navigate('/competitions');
  }

  return (
    <Fragment>
      <PageLayout.Context
        items={[
          ...insertOnCondition(
            <ContextMenu.Item
              onClick={() => competition?.api_key && powerlifting.integrate(competition.api_key)}
              label="Integrera Powerlifting Live"
              key="integration-powerlifting-live"
            />,
            !isResultsPage && can('manage-competition-integrations', competition?.id)
          ),
          ...insertOnCondition(
            <ContextMenu.Item label="Visa resultat" to={`/competitions/${id}/results`} key="result" />,
            !isResultsPage
          ),
          ...insertOnCondition(
            <ContextMenu.Item
              label="Hantera resultat"
              to={`/competitions/${id}/results/manage`}
              key="manage-results"
            />,
            can('manage-competition-result', id)
          ),
          ...insertOnCondition(
            <ContextMenu.Item label="Redigera tävling" to={`/competitions/${id}/edit`} key="edit" />,
            can('edit-competition', id)
          ),
          ...insertOnCondition(
            <ContextMenu.Item label="Exportera CSV" onClick={handleExport} key="export" />,
            can('export-competition-registrations', id)
          ),
          ...insertOnCondition(
            <ContextMenu.Item label="Ta bort" onClick={dialog.open} key="delete" danger />,
            can('delete-competition', id)
          ),
        ]}
      />

      <ConfirmDialog isLoading={deleteMutation.isPending} onConfirm={handleDelete} {...dialog} />
    </Fragment>
  );
}
